import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calendar, Clock, ChevronRight, Search, Shield, Sun, Moon, User, LogOut, MessageSquare, Settings } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import Footer from './Footer';
import { getAllPosts } from '../utils/blogUtils';

const Blog = () => {
  const { theme, toggleTheme } = useTheme();
  const blogPosts = getAllPosts();

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Predica.AI Blog",
    "description": "Latest articles and tutorials about AI-powered document analysis, processing, and insights.",
    "url": "https://predica.ai/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Predica.AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://predica.ai/logo.png"
      }
    },
    "blogPost": blogPosts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.frontMatter.title,
      "description": post.frontMatter.excerpt,
      "datePublished": post.frontMatter.date,
      "author": {
        "@type": "Person",
        "name": post.frontMatter.author.name
      }
    }))
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Helmet>
        <title>AI Document Analysis Blog | Tutorials & Best Practices | Predica.AI</title>
        <meta name="description" content="Learn about AI-powered document analysis, processing techniques, and best practices. Tutorials and guides for getting the most out of your documents." />
        <meta name="keywords" content="document analysis, AI tutorials, document processing, machine learning, OCR, data extraction" />
        <link rel="canonical" href="https://predica.ai/blog" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI Document Analysis Blog | Tutorials & Best Practices" />
        <meta property="og:description" content="Learn about AI-powered document analysis, processing techniques, and best practices." />
        <meta property="og:url" content="https://predica.ai/blog" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Document Analysis Blog | Tutorials & Best Practices" />
        <meta name="twitter:description" content="Learn about AI-powered document analysis, processing techniques, and best practices." />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-gray-200 dark:border-gray-800 bg-white/75 dark:bg-gray-900/75 backdrop-blur-lg">
        <div className="flex h-16 items-center px-4 sm:px-8">
          <div className="flex items-center space-x-4">
            <Link to="/" className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-blue-500" />
              <span className="text-xl font-bold text-gray-900 dark:text-white">Predica.AI</span>
            </Link>
          </div>

          <div className="ml-auto flex items-center space-x-4">
            <button
              onClick={toggleTheme}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              {theme === 'dark' ? (
                <Sun className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <Moon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative rounded-full overflow-hidden hover:ring-2 hover:ring-blue-500/20 transition-all duration-300">
                  <User className="h-8 w-8 text-gray-600 dark:text-gray-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main content with adjusted padding for header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-24">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400 text-transparent bg-clip-text">
              Predica.AI Blog
            </span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Discover tutorials, best practices, and insights about AI-powered document analysis
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <input
              type="text"
              placeholder="Search articles..."
              className="w-full px-4 py-3 pl-12 rounded-xl
                bg-white dark:bg-gray-800 
                border border-gray-200 dark:border-gray-700
                focus:outline-none focus:ring-2 focus:ring-purple-500
                text-gray-900 dark:text-white"
            />
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
        </div>

        {/* Featured Post */}
        <div className="mb-16">
          <Link 
            to={`/blog/${blogPosts[0].slug}`}
            className="group block bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300"
          >
            <div className="relative h-96">
              <img
                src={blogPosts[0].frontMatter.image}
                alt={blogPosts[0].frontMatter.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <span className="inline-block px-4 py-1 rounded-full bg-purple-500 text-white text-sm font-medium mb-4">
                  {blogPosts[0].frontMatter.category}
                </span>
                <h2 className="text-3xl font-bold text-white mb-2">
                  {blogPosts[0].frontMatter.title}
                </h2>
                <p className="text-gray-200 mb-4">
                  {blogPosts[0].frontMatter.excerpt}
                </p>
                <div className="flex items-center text-white/80">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span className="mr-4">{new Date(blogPosts[0].frontMatter.date).toLocaleDateString()}</span>
                  <Clock className="w-4 h-4 mr-2" />
                  <span>{blogPosts[0].frontMatter.readTime}</span>
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.slice(1).map(post => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="relative h-48">
                <img
                  src={post.frontMatter.image}
                  alt={post.frontMatter.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <span className="inline-block px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-300 text-sm font-medium mb-4">
                  {post.frontMatter.category}
                </span>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                  {post.frontMatter.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {post.frontMatter.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <img
                      src={post.frontMatter.author.avatar}
                      alt={post.frontMatter.author.name}
                      className="w-8 h-8 rounded-full mr-2"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {post.frontMatter.author.name}
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <Clock className="w-4 h-4 mr-1" />
                    <span>{post.frontMatter.readTime}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Blog; 